import { c as _c } from "react/compiler-runtime";
import { Form } from '@onefootprint/ui';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGetNodeConfigContext } from '@/components/playbook-builder/hooks/use-get-node-config-context';
import { CelNodeFormSubtitle } from '../../shared/cel-node-form-instructions';
import ExpandableExpressionEditor from '../../shared/expandable-expression-editor';
import { expectedInputSides, type InputDocumentSide, toDocumentTypesAndCountries } from '../id-doc-kinds';
import type { FormData } from './id-docs.types';
const SIDE_FIELD = {
  front: 'inputs.front',
  back: 'inputs.back',
  selfie: 'inputs.selfie'
} as const;

/**
 * Points the node at documents this onboarding already holds, verified in place of asking the user
 * to upload anything. Each side takes a CEL expression that must resolve to a document. The sides
 * offered come from the drawer's own settings rather than the saved ones: the backend requires input
 * expressions to cover exactly the sides the node's acceptable kinds ask for.
 */
const DocumentInputsSection = t0 => {
  const $ = _c(46);
  const {
    nodeId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "collectDocument.inputs"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const config = useGetNodeConfigContext(nodeId, "templatable");
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      name: "useInputs"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isEnabled = useWatch(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      name: "scope"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const scope = useWatch(t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      name: "global"
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const global = useWatch(t4);
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      name: "country"
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const country = useWatch(t5);
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      name: "collectSelfie"
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const collectSelfie = useWatch(t6);
  let t10;
  let t11;
  let t7;
  let t8;
  let t9;
  if ($[6] !== collectSelfie || $[7] !== config?.autocomplete || $[8] !== config?.languageDefinition || $[9] !== country || $[10] !== global || $[11] !== isEnabled || $[12] !== scope || $[13] !== t) {
    const sides = expectedInputSides({
      ...toDocumentTypesAndCountries({
        scope,
        global,
        country
      }),
      collectSelfie
    });
    const labels = {
      front: t(_temp),
      back: t(_temp2),
      selfie: t(_temp3)
    };
    t10 = "flex flex-col gap-3";
    let t12;
    if ($[19] !== t) {
      t12 = t(_temp4);
      $[19] = t;
      $[20] = t12;
    } else {
      t12 = $[20];
    }
    let t13;
    if ($[21] !== t12) {
      t13 = <div className="text-label-2 text-primary">{t12}</div>;
      $[21] = t12;
      $[22] = t13;
    } else {
      t13 = $[22];
    }
    let t14;
    if ($[23] !== t) {
      t14 = t(_temp5);
      $[23] = t;
      $[24] = t14;
    } else {
      t14 = $[24];
    }
    let t15;
    if ($[25] !== t14) {
      t15 = <div className="text-body-2 text-tertiary">{t14}</div>;
      $[25] = t14;
      $[26] = t15;
    } else {
      t15 = $[26];
    }
    if ($[27] !== t13 || $[28] !== t15) {
      t11 = <div className="flex flex-col gap-1">{t13}{t15}</div>;
      $[27] = t13;
      $[28] = t15;
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    t7 = "flex flex-col gap-4 p-3 border border-tertiary rounded-md";
    let t16;
    if ($[30] !== t) {
      t16 = t(_temp6);
      $[30] = t;
      $[31] = t16;
    } else {
      t16 = $[31];
    }
    let t17;
    if ($[32] !== isEnabled || $[33] !== t) {
      t17 = isEnabled ? t(_temp7) : undefined;
      $[32] = isEnabled;
      $[33] = t;
      $[34] = t17;
    } else {
      t17 = $[34];
    }
    if ($[35] !== t16 || $[36] !== t17) {
      t8 = <Form.Toggle name="useInputs" label={t16} hint={t17} size="compact" />;
      $[35] = t16;
      $[36] = t17;
      $[37] = t8;
    } else {
      t8 = $[37];
    }
    t9 = isEnabled && <div className="flex flex-col gap-3">{sides.map(side => <fieldset key={side} aria-label={labels[side]} className="flex items-center gap-3"><div className="flex-1 min-w-0 flex items-center min-h-8 text-label-3 text-primary">{labels[side]}</div><div className="flex-1 min-w-0"><Controller name={SIDE_FIELD[side]} rules={{
            required: t(_temp8)
          }} render={t18 => {
            const {
              field,
              fieldState
            } = t18;
            return <div className="flex flex-col gap-1"><ExpandableExpressionEditor value={field.value ?? ""} onChange={field.onChange} disabled={field.disabled} autocompleteItems={config?.autocomplete} languageDefinition={config?.languageDefinition} placeholder={t(_temp9)} hasError={!!fieldState.error} labels={{
                expand: t(_temp10),
                dialogTitle: labels[side],
                subtitle: <CelNodeFormSubtitle i18nKey={_temp11} />,
                celSubtitle: t(_temp12),
                instructions: t(_temp13)
              }} />{fieldState.error && <span className="text-error text-caption-2">{fieldState.error.message}</span>}</div>;
          }} /></div></fieldset>)}</div>;
    $[6] = collectSelfie;
    $[7] = config?.autocomplete;
    $[8] = config?.languageDefinition;
    $[9] = country;
    $[10] = global;
    $[11] = isEnabled;
    $[12] = scope;
    $[13] = t;
    $[14] = t10;
    $[15] = t11;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t10 = $[14];
    t11 = $[15];
    t7 = $[16];
    t8 = $[17];
    t9 = $[18];
  }
  let t12;
  if ($[38] !== t7 || $[39] !== t8 || $[40] !== t9) {
    t12 = <div className={t7}>{t8}{t9}</div>;
    $[38] = t7;
    $[39] = t8;
    $[40] = t9;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== t10 || $[43] !== t11 || $[44] !== t12) {
    t13 = <section className={t10}>{t11}{t12}</section>;
    $[42] = t10;
    $[43] = t11;
    $[44] = t12;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  return t13;
};
export default DocumentInputsSection;
function _temp($) {
  return $.documents.sides.front;
}
function _temp2($_0) {
  return $_0.documents.sides.back;
}
function _temp3($_1) {
  return $_1.documents.sides.selfie;
}
function _temp4($_2) {
  return $_2.title;
}
function _temp5($_3) {
  return $_3.subtitle;
}
function _temp6($_4) {
  return $_4.documents.toggle.label;
}
function _temp7($_5) {
  return $_5.documents.toggle.hint;
}
function _temp8($_6) {
  return $_6.documents.expression.required;
}
function _temp9($_7) {
  return $_7.documents.expression.placeholder;
}
function _temp10($_8) {
  return $_8.documents.expression.expand;
}
function _temp11($_9) {
  return $_9.collectDocument.inputs.documents.celInstructions;
}
function _temp12($_10) {
  return $_10.documents.celSubtitle;
}
function _temp13($_11) {
  return $_11.documents.instructions;
}